import { motion } from "framer-motion";
import { ExternalLink, FlaskConical, Target, Brain, Home, Scale, CheckCircle, Clock, Search, GitFork, Shirt } from "lucide-react";
import { Button } from "@/components/ui/button";

const projects = [
  {
    id: 1,
    title: "ArcheryManagerAI",
    subtitle: "AI-Powered Sports Operations Dashboard",
    status: "live",
    url: "https://www.archerymanagerai.com/",
    description: "An AI-driven tournament management platform designed to optimize registration workflows, automate line assignments, and improve event coordination through structured operational logic.",
    focus: [
      "Intelligent participant scheduling",
      "Automated line allocation and fairness controls",
      "Real-time operational dashboards",
      "Data-driven tournament oversight"
    ],
    signal: "AI-enabled production system supporting live competitive events.",
    icon: Target
  },
  {
    id: 2,
    title: "Interview Simulator",
    subtitle: "Structured AI Evaluation System",
    status: "live",
    url: "https://interview.vasanthnatarajan.me/",
    description: "A multi-panel AI system that simulates technical interviews and delivers actionable, structured feedback.",
    capabilities: [
      "Evaluation scoring framework",
      "Strength and gap analysis",
      "Follow-up question generation",
      "Personalized improvement roadmap"
    ],
    designPrinciple: "Evaluation-first architecture with structured outputs over conversational responses.",
    icon: Brain
  },
  {
    id: 3,
    title: "RAG Debugger",
    subtitle: "Retrieval-Augmented Generation Analysis Tool",
    status: "live",
    url: "https://www.ragdebugger.com/",
    description: "A debugging and analysis platform for RAG pipelines, helping developers identify retrieval issues, evaluate chunk quality, and optimize embedding performance.",
    capabilities: [
      "Chunk retrieval visualization",
      "Embedding quality analysis",
      "Query-document relevance scoring",
      "Pipeline performance metrics"
    ],
    signal: "Essential tooling for production RAG systems requiring observability and debugging.",
    icon: Search
  },
  {
    id: 4,
    title: "ArchInsights",
    subtitle: "AI Architecture Analysis Platform",
    status: "live",
    url: "https://archinsights.vasanthnatarajan.me/",
    description: "An intelligent platform that analyzes system architectures and provides actionable insights for optimization, scalability, and best practices alignment.",
    capabilities: [
      "Architecture pattern recognition",
      "Scalability assessment",
      "Best practices recommendations",
      "Technical debt identification"
    ],
    signal: "AI-powered architecture review for modern distributed systems.",
    icon: Brain
  },
  {
    id: 5,
    title: "Unlived",
    subtitle: "Parallel Life Simulation Engine",
    status: "live",
    url: "https://www.unlived.app/",
    description: "An AI-powered app that simulates the parallel lives you could have lived based on decisions you didn't take — and shows you how to shift toward the life you still want.",
    capabilities: [
      "Fork Score calculation",
      "Dual timeline simulation",
      "Regret forecast modeling",
      "Bridge plan generation"
    ],
    signal: "Plausible life trajectory simulation helping users understand the impact of unmade decisions.",
    icon: GitFork
  },
  {
    id: 6,
    title: "FitVerdict",
    subtitle: "AI Outfit Judge & Style Analyzer",
    status: "live",
    url: "https://fitverdict.app/",
    description: "Snap your outfit and get an instant AI verdict — APPROVED, PASSED, RETRIAL, or DENIED. Layer-by-layer scoring with witty rulings and actionable upgrade suggestions.",
    capabilities: [
      "Instant outfit scoring",
      "Layer-by-layer analysis",
      "Occasion-based judging",
      "Style battle mode"
    ],
    signal: "Privacy-first fashion AI — photos judged then destroyed, never stored.",
    icon: Shirt
  },
  {
    id: 7,
    title: "Mortgage Underwriting Assistant",
    subtitle: "Consumer Risk Interpretation Tool",
    status: "in-progress",
    description: "An AI system translating underwriting logic into clear, actionable guidance for homebuyers.",
    capabilities: [
      "Debt-to-income analysis",
      "Credit profile interpretation",
      "Risk categorization",
      "Pre-application improvement insights"
    ],
    objective: "Increase financial clarity through structured AI interpretation.",
    icon: Home
  },
  {
    id: 8,
    title: "Immigration Attorney Helper",
    subtitle: "Legal Process Navigation System",
    status: "in-progress",
    description: "An AI assistant designed to help navigate complex immigration processes with structured guidance.",
    capabilities: [
      "Document requirement analysis",
      "Timeline estimation",
      "Process step guidance",
      "FAQ interpretation"
    ],
    objective: "Simplify immigration complexity through AI-powered guidance.",
    icon: Scale
  }
];

export default function AILabsPage() {
  return (
    <div className="pt-20" data-testid="ai-labs-page">
      {/* Hero Section */}
      <section className="py-24 md:py-32 hero-gradient grain">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <div className="flex items-center gap-3 mb-6">
              <FlaskConical className="text-royal-blue-800" size={32} />
              <span className="text-xs font-semibold tracking-[0.2em] uppercase text-royal-blue-800">
                AI Labs
              </span>
            </div>
            <h1 className="font-display text-4xl md:text-5xl font-bold text-zinc-950 mb-6">
              Applied AI Systems for Real-World Execution
            </h1>
            <p className="text-lg text-zinc-600 leading-relaxed">
              I design and build practical AI systems that address operational, financial, and workflow inefficiencies. Each initiative reflects structured engineering, domain depth, and production-oriented thinking.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Projects Section */}
      <section className="py-24 md:py-32 bg-white" data-testid="projects-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="space-y-16">
            {projects.map((project, index) => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="border-b border-zinc-200 pb-16 last:border-0"
                data-testid={`project-${project.id}`}
              >
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  {/* Project Header */}
                  <div className="lg:col-span-1">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="skill-icon">
                        <project.icon size={24} />
                      </div>
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <h2 className="font-display text-2xl font-semibold text-zinc-950">
                            {project.title}
                          </h2>
                          {project.status === "live" ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                              <CheckCircle size={12} />
                              Live
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                              <Clock size={12} />
                              In Progress
                            </span>
                          )}
                        </div>
                        <p className="text-royal-blue-800 font-medium">
                          {project.subtitle}
                        </p>
                      </div>
                    </div>
                    
                    {project.url && (
                      <a
                        href={project.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 mt-4"
                      >
                        <Button
                          variant="outline"
                          className="border-royal-blue-800 text-royal-blue-800 hover:bg-royal-blue-800 hover:text-white"
                        >
                          Access Project
                          <ExternalLink size={16} className="ml-2" />
                        </Button>
                      </a>
                    )}
                  </div>

                  {/* Project Details */}
                  <div className="lg:col-span-2">
                    <p className="text-zinc-600 mb-6 leading-relaxed">
                      {project.description}
                    </p>

                    <div className="mb-6">
                      <h3 className="font-semibold text-zinc-950 mb-3">
                        {project.focus ? "Focus" : "Capabilities"}
                      </h3>
                      <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {(project.focus || project.capabilities).map((item, i) => (
                          <li key={`${project.id}-capability-${i}`} className="flex items-start gap-2 text-zinc-600">
                            <CheckCircle className="text-royal-blue-800 flex-shrink-0 mt-1" size={16} />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {(project.signal || project.designPrinciple || project.objective) && (
                      <div className="p-4 bg-zinc-50 border-l-4 border-royal-blue-800 rounded-r">
                        <p className="text-sm font-medium text-zinc-950 mb-1">
                          {(() => {
                            if (project.signal) return "Signal";
                            if (project.designPrinciple) return "Design Principle";
                            return "Objective";
                          })()}
                        </p>
                        <p className="text-zinc-600">
                          {project.signal || project.designPrinciple || project.objective}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 cta-section" data-testid="labs-cta">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <FlaskConical className="mx-auto mb-6 opacity-80" size={48} />
          <h2 className="font-display text-3xl md:text-4xl font-semibold mb-6">
            Interested in Collaborating?
          </h2>
          <p className="text-blue-100 text-lg mb-8 max-w-xl mx-auto">
            If you're building AI systems for real-world applications, I'd love to exchange ideas.
          </p>
          <a href="/book">
            <Button className="bg-white text-royal-blue-800 hover:bg-blue-50 px-8 py-6 text-xs tracking-wider uppercase">
              Start a Conversation
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
}
